import { useTranslations } from "@zapier/i18n";
import { useContext, useMemo, useEffect, useCallback } from "react";
import { Icon } from "@zapier/zinnia-icons";
import {
  BetaDataTableContext,
  BetaPageHeaderBase,
  Button,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import {
  useFolderAncestors,
  FolderTreeItem,
  HydratedAsset,
  useCurrentUser,
  useMembers,
  useFolderActionPermissions,
  useSupportedAssets,
} from "@zapier/client";
import {
  DEFAULT_FOLDER_ID,
  FOLDER_TRASH_ID,
  getCreateZapUrl,
  getVirtualFolderName,
  isVirtualFolder,
} from "@zapier/utils";
import {
  KebabMenu,
  useFolderAndZapsModalStates,
  useRouterParams,
  FolderModal,
  CopyToAnotherAccountModal,
} from "@zapier/shared";
import styles from "./FolderHeader.module.css";
import { FolderBreadcrumbs } from "../FolderBreadcrumbs";
import { FolderHeaderTitle } from "../FolderHeaderTitle";
import { FolderHeaderSharing } from "../FolderHeaderSharing";
import { CreationButton } from "../CreationButton";
import { useFolderActionItems } from "../../hooks/useFolderActionItems";
import { FolderModals } from "../FolderModals";

interface FolderHeaderProps {
  folder?: FolderTreeItem;
  folderId: string;
  isFoldersLoading?: boolean;
  assets?: HydratedAsset[];
}

const FolderHeader = ({
  folder,
  folderId,
  isFoldersLoading,
  assets = [],
}: FolderHeaderProps) => {
  const t = useTranslations("folders-page-header");

  const { isMPFEnabled } = useSupportedAssets();
  const { memberId } = useRouterParams();
  const { user, isLoading: isUserLoading } = useCurrentUser();
  const { clearSelection, selectedRows = [] } =
    useContext(BetaDataTableContext);

  const {
    currentFolder,
    setCurrentFolder,
    currentFolderModal,
    setCurrentFolderModal,
  } = useFolderAndZapsModalStates();

  const { ancestors, isAncestorsLoading } = useFolderAncestors({
    folderId,
  });

  const { enablePermissionsCheck, canShare } = useFolderActionPermissions({
    folderId,
    folderDepth: ancestors?.length || 0,
  });

  const { members, isLoading: isMembersLoading } = useMembers({
    ids: memberId ? [Number(memberId)] : [],
  });

  const folderName = useMemo(() => {
    if (folder?.name) return folder?.name;

    // Handle virtual folders with centralized logic
    if (folderId && isVirtualFolder(folderId)) {
      return getVirtualFolderName(folderId, {
        user,
        memberId,
        members,
      });
    }

    return "";
  }, [folder?.name, folderId, memberId, members, user]);

  const icon = folderId === FOLDER_TRASH_ID ? "actionTrash" : undefined;

  const folderActionItems = useFolderActionItems({
    folder,
    folderLevel: ancestors?.length || 0,
    setCurrentFolder,
    setCurrentModal: setCurrentFolderModal,
  });

  // Use centralized logic for breadcrumb folder names
  const breadcrumbFolderName = useMemo(() => {
    if (folderId && isVirtualFolder(folderId)) {
      return getVirtualFolderName(folderId, {
        user,
        memberId,
        members,
      });
    }
    return folder?.name;
  }, [folder?.name, folderId, memberId, members, user]);

  useEffect(() => {
    enablePermissionsCheck();
  }, [enablePermissionsCheck]);

  const handleOpenFolderHeaderMenu = useCallback(() => {
    if (selectedRows.length > 0) {
      clearSelection?.();
    }
  }, [clearSelection, selectedRows.length]);

  return (
    <>
      <BetaPageHeaderBase
        topRowContent={
          <FolderBreadcrumbs
            ancestors={ancestors}
            currentFolderName={breadcrumbFolderName}
            isAncestorsLoading={isAncestorsLoading}
            hasAssetsRoot={isMPFEnabled}
            user={user}
            memberId={memberId}
            members={members}
          />
        }
      >
        <div className={styles.folderHeader}>
          <FolderHeaderTitle
            icon={icon}
            title={folderName}
            folderId={folderId}
            isTrashed={folderId === FOLDER_TRASH_ID}
            isFoldersLoading={
              isFoldersLoading || isUserLoading || isMembersLoading
            }
            isShared={folder?.isShared}
          />

          {folderId !== FOLDER_TRASH_ID && (
            <div className={styles.folderHeaderInfo}>
              <FolderHeaderSharing />
              {canShare && (
                <Button
                  iconPosition="start"
                  onPress={() => {
                    setCurrentFolder(folder);
                    setCurrentFolderModal(FolderModal.Share);
                  }}
                  onPressEnd={() => {}}
                  onPressStart={() => {}}
                  size="medium"
                  variant="tertiary"
                >
                  <Icon aria-hidden name="personAdd" />
                  {t("buttons.share")}
                </Button>
              )}
              <CreationButton
                createZapHref={getCreateZapUrl(folderId)}
                parentId={folder?.id ?? folderId}
                parentName={folder?.name}
                parentIsShared={folder?.isShared}
                folderDepth={(ancestors?.length || 0) + 1}
              />

              {folderId !== DEFAULT_FOLDER_ID && (
                <Tooltip
                  allowMultiline={true}
                  content={t("tooltips.actions")}
                  key="kebab-folderHeader"
                  placement="bottom"
                >
                  <TooltipFocusableTrigger>
                    <KebabMenu
                      menuLabel={t("menus.folder-options")}
                      buttonLabel={t(
                        "accessibility.toggle-folder-options-menu",
                      )}
                      menuItems={folderActionItems}
                      onOpenMenu={handleOpenFolderHeaderMenu}
                      size="medium"
                    />
                  </TooltipFocusableTrigger>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </BetaPageHeaderBase>
      <FolderModals
        currentModal={currentFolderModal}
        setCurrentModal={setCurrentFolderModal}
        currentFolder={currentFolder}
        setCurrentFolder={setCurrentFolder}
      />
      {/* Copy needs a concrete folder: virtual roots excluded via isVirtualFolder (home/trash names + ids) and 0/-1. */}
      <CopyToAnotherAccountModal
        isOpen={currentFolderModal === FolderModal.CopyToAnotherAccount}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setCurrentFolderModal(undefined);
            setCurrentFolder(undefined);
          }
        }}
        assets={assets}
        copyEntryPoint="top_nav_kebab"
        folderIds={
          !isVirtualFolder(folderId) &&
          folderId !== DEFAULT_FOLDER_ID &&
          folderId !== FOLDER_TRASH_ID
            ? [folderId]
            : undefined
        }
      />
    </>
  );
};

export default FolderHeader;
